import React from 'react';
import { validateCronPattern } from './api.js';

export type CronValidation = {
  preview: string[];
  validating: boolean;
  error: string | null;
  /** True once a response has landed for the CURRENT pattern/timezone — gate on
   * this rather than `preview.length` alone, since preview/error can otherwise lag
   * one edit behind the inputs. */
  settled: boolean;
};

export type UseCronValidationOptions = {
  pattern: string;
  timezone: string;
  enabled: boolean;
};

/**
 * Debounced cron pattern validation shared by the create and edit schedule dialogs.
 * Calls the `/schedules/validate-cron` endpoint 500ms after the pattern/timezone
 * settle, returning the next 5 run times on success or an error message on failure.
 */
export function useCronValidation({ pattern, timezone, enabled }: UseCronValidationOptions): CronValidation {
  const [preview, setPreview] = React.useState<string[]>([]);
  const [error, setError] = React.useState<string | null>(null);
  // The pattern/timezone the last completed (non-stale) validation ran against.
  // `settled` below compares this to the current inputs, so it flips to false
  // the instant the inputs change and back to true once a matching response lands.
  const [settledFor, setSettledFor] = React.useState<{ pattern: string; timezone: string } | null>(
    null
  );

  React.useEffect(() => {
    if (!enabled || !pattern) {
      setPreview([]);
      setError(null);
      setSettledFor(null);
      return;
    }

    let cancelled = false;

    const timeoutId = setTimeout(async () => {
      setError(null);

      try {
        const data = await validateCronPattern(pattern, timezone, 5);
        if (cancelled) return;
        setSettledFor({ pattern, timezone });
        if (data.valid && data.nextRuns) {
          setPreview(data.nextRuns);
        } else {
          setPreview([]);
          setError(data.error || 'Invalid cron pattern');
        }
      } catch {
        if (cancelled) return;
        setSettledFor({ pattern, timezone });
        setPreview([]);
        setError('Failed to validate cron pattern');
      }
    }, 500);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [pattern, timezone, enabled]);

  const settled = settledFor !== null && settledFor.pattern === pattern && settledFor.timezone === timezone;
  // Derived, not stored: there's no state to get stuck mid-request, and it goes true
  // the instant an input changes rather than only once the debounce timer fires.
  const validating = enabled && Boolean(pattern) && !settled;

  return { preview, validating, error, settled };
}
